import React, { useState } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';
import { FormComponentProps, DocumentsData } from '../../types/forms';

interface FileUploadProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  accept: string;
  onFileSelect: (files: FileList | null) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ title, description, icon, accept, onFileSelect }) => {
  const [files, setFiles] = useState<File[]>([]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles) {
      setFiles(Array.from(selectedFiles));
      onFileSelect(selectedFiles);
    }
  };

  return (
    <Paper
      sx={{
        p: 4,
        textAlign: 'center',
        border: '2px dashed #e0e0e0',
        borderRadius: 2,
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      }}
      onClick={() => document.getElementById(`file-upload-${title}`)?.click()}
    >
      <input
        type="file"
        id={`file-upload-${title}`}
        multiple
        accept={accept}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <Box sx={{ mb: 2 }}>{icon}</Box>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {description}
      </Typography>
      {files.length > 0 && (
        <Box sx={{ mt: 2, textAlign: 'left' }}>
          {files.map((file, index) => (
            <Typography key={index} variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon fontSize="small" />
              {file.name}
            </Typography>
          ))}
        </Box>
      )}
    </Paper>
  );
};

const DocumentUpload: React.FC<FormComponentProps<DocumentsData>> = ({ data, onNext }) => {
  const [documents, setDocuments] = useState<DocumentsData['documents']>(data?.documents || []);

  const handleFileSelect = (files: FileList | null, type: string) => {
    if (files) {
      const newDocuments = Array.from(files).map(file => ({
        type,
        file,
        uploaded: false
      }));
      const updatedDocuments = [...documents, ...newDocuments];
      setDocuments(updatedDocuments);
      onNext({ documents: updatedDocuments });
    }
  };

  return (
    <Box>
      <Typography variant="h3" gutterBottom sx={{ fontWeight: 400 }}>
        Document Upload
      </Typography>
      <Typography variant="h6" color="text.secondary" paragraph sx={{ fontWeight: 400 }}>
        Upload your tax documents securely. You can upload multiple files at once.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <FileUpload
          title="Upload Tax Documents"
          description="W-2s, 1099s, 1098s, receipts, prior year returns, and other tax documents"
          icon={<CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          onFileSelect={(files) => handleFileSelect(files, 'tax')}
        />

        <FileUpload
          title="Upload Business Records"
          description="Business expense receipts, profit/loss statements, business records"
          icon={<BusinessIcon sx={{ fontSize: 48, color: 'primary.main' }} />}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
          onFileSelect={(files) => handleFileSelect(files, 'business')}
        />

        <FileUpload
          title="Upload Identity Documents"
          description="Driver's license, Social Security cards, government-issued ID"
          icon={<BadgeIcon sx={{ fontSize: 48, color: 'primary.main' }} />}
          accept=".pdf,.jpg,.jpeg,.png"
          onFileSelect={(files) => handleFileSelect(files, 'identity')}
        />
      </Box>
    </Box>
  );
};

export default DocumentUpload; 